import { CONFIG } from "../config";
import { campagneCourante } from "./campagneCourante";
import { marquerParcoursEngage, relayerMeta } from "./meta";
import { sidDurable } from "./identite";

/**
 * Instrumentation. Aucune donnée nominative dans `props` : le champ `sid`
 * suffit à reconstituer un parcours complet, sans cookie ni identification.
 */
export type EventName =
  | "view"
  | "cta_click"
  | "diagnostic_start"
  | "model_ok"
  | "model_retry"
  | "model_fallback"
  | "cache_hit"
  | "referral_landing"
  // Arrivée portant des paramètres de campagne (utm) : c'est ce qui permettra
  // de juger la première annonce payante en francs dépensés par diagnostic.
  | "campagne_arrivee"
  | "score_shown"
  /*
    L'entre-deux du mode métier, en quatre marches — 11/10/2026.

    Trois visiteurs sur quatre qui tapent leur métier ne voyaient jamais
    leur indice, et rien ne disait où ils partaient : pendant l'attente de
    la génération, sur une erreur, ou devant l'écran des tâches. Ces quatre
    évènements le disent.
  */
  | "metier_lance"
  | "metier_erreur"
  | "taches_affichees"
  | "taches_validees"
  // Les deux volets facultatifs, ouverts et terminés. Le miroir exact de
  // NOMS_ATTENDUS côté serveur : un nom qui n'y figure pas est rejeté.
  | "volet2_start"
  | "volet2_done"
  | "volet3_start"
  | "volet3_done"
  // L'axe dirigeant. Comptés séparément de ceux du salarié : les deux parcours
  // n'ont ni la même longueur, ni le même prix, ni le même taux d'abandon
  // attendu, et les mélanger rendrait les deux entonnoirs illisibles.
  | "dirigeant_demarre"
  | "dirigeant_questionnaire"
  | "dirigeant_resultat"
  // Les deux évènements qui jugent les coefficients sectoriels — la limite la
  // plus sérieuse de l'axe. `dirigeant_estime` : le résultat affiché repose
  // sur le barème non sourcé, faute de salaire déclaré. `dirigeant_affine` :
  // ce même résultat a ensuite été corrigé avec un salaire réel. L'écart des
  // deux mesure combien de personnes voient réellement un montant assis sur
  // les onze coefficients — et donc l'urgence, ou non, de les sourcer.
  | "dirigeant_estime"
  | "dirigeant_affine"
  // Le dirigeant transmet le diagnostic salarié à son équipe. C'est
  // l'évènement qui juge l'arbitrage du 18/08 : l'axe dirigeant est d'abord
  // un canal d'acquisition pour l'axe salarié, et rien d'autre ne dit s'il
  // remplit ce rôle. À lire avec `referral_landing`, qui compte les arrivées
  // que ces partages provoquent.
  | "partage_equipe"
  // Combien de visiteurs se déclarent à leur compte. C'est ce qui dira si la
  // troisième porte de l'accueil sert à quelqu'un — et, rapporté aux ventes,
  // si un indépendant achète au même taux qu'un salarié.
  | "statut_independant"
  | "scoring_group_fallback"
  | "share"
  // Partage de la carte en image : le geste que l'on cherche à provoquer, donc
  // celui qu'il faut pouvoir compter séparément d'un partage de lien.
  | "share_image"
  | "carte_enregistree"
  // Un plan qui n'arrive pas est un achat qui ne se fera pas : il se compte.
  | "plan_indisponible"
  // Le bandeau de consentement Meta : affiché, accepté, refusé. Sans ces trois
  // compteurs, on ignore quelle part du trafic Meta peut voir — et donc si la
  // campagne « ventes » optimise sur quelque chose. Anonymes, comme le reste.
  | "meta_bandeau_vu"
  | "meta_accepte"
  | "meta_refuse"
  | "copy_link"
  | "download_result"
  | "ics_download"
  | "recuperation_tentee"
  | "recuperation_reussie"
  | "capture_submit"
  | "pay_open"
  // Une transaction déjà engagée, rouverte : pas une ouverture (12/10/2026).
  | "pay_reprise"
  // La question de sortie de la caisse (12/10/2026).
  | "retenu_prix"
  | "retenu_apercu"
  | "retenu_plus_tard"
  | "retenu_paiement"
  | "retenu_confiance"
  | "pay_operator"
  | "pay_submit"
  | "pay_success"
  | "pay_error"
  | "pay_close"
  | "faq_open"
  // La carte du temps récupéré, offerte à l'acheteur. Comptée séparément de la
  // carte d'indice : les deux ne partent pas du même écran, ne s'adressent pas
  // aux mêmes personnes, et les mélanger rendrait illisible celle des deux qui
  // ramène réellement quelqu'un.
  | "carte_gain_partage"
  | "carte_gain_enregistree"
  // Quelqu'un a effacé ce que son appareil gardait, depuis les mentions de
  // confidentialité. Compté pour savoir si l'identifiant durable gêne — un
  // réglage qu'on offre sans jamais regarder s'il sert n'est pas un réglage.
  | "appareil_oublie"
  /*
    Les demandes de support, comptées par motif — 26/08/2026.

    L'axe 98 demandait « une trace des demandes ». Jusqu'ici il n'y en avait
    aucune : une adresse dans le pied de page, une boîte relevée à la main, et
    rien qui dise combien de gens écrivent ni pourquoi. Un support qu'on ne
    mesure pas ne se dimensionne pas — et surtout, le motif dominant est
    l'information la plus utile du produit : si les demandes portent en
    majorité sur un rapport qui n'arrive pas, ce n'est pas le support qu'il
    faut renforcer, c'est la livraison.

    Quatre noms écrits d'avance, et non un nom construit à partir d'un motif
    saisi : les clés de compteur viennent du code, jamais d'une entrée.
  */
  | "support_rapport"
  | "support_remboursement"
  | "support_donnees"
  | "support_autre"
  // Le rapport de progression (28/09/2026) : page ouverte, engagement pris,
  // mesure faite. La vente, elle, est comptée par la livraison.
  | "progression_ouverte"
  | "progression_engagee"
  | "progression_mesuree"
  // Le point d'étape à trente jours : une réponse, un nom. Les clés de
  // compteur viennent du code, jamais d'une valeur transmise.
  | "etape_commencee"
  | "etape_pas_encore"
  | "etape_abandonnee"
  // La recommandation : la note par tranche, et le message prérempli ouvert.
  | "note_promoteur"
  | "note_passif"
  | "note_detracteur"
  | "recommandation_envoyee";

export interface TrackedEvent {
  name: EventName;
  ts: string;
  sid: string;
  props: Record<string, unknown>;
}

/**
 * Identifiant de cet appareil.
 *
 * Il était tiré au hasard **et jamais persisté**, ce qui rendait le parrainage
 * mathématiquement impossible : le lien de parrainage l'embarque, et un parrain
 * qui fermait son onglet devenait quelqu'un d'autre. La décision, ce qu'elle
 * change et ce qu'elle ne change pas sont dans `identite.ts`.
 *
 * En recette il reste tiré à neuf à chaque fois, et préfixé, pour que le
 * serveur écrive ces évènements sans les compter.
 */
export const SID = sidDurable();

/** File locale, conservée tant qu'aucune adresse de collecte n'est renseignée. */
export const EVENTS: TrackedEvent[] = [];

export function track(name: EventName, props: Record<string, unknown> = {}): void {
  // La campagne de la session accompagne chaque évènement : le serveur en
  // fait des compteurs par annonce, sans rien de plus sur la personne. Elle
  // est posée **après** les propriétés : c'est l'identifiant réduit qui
  // compte, et aucun libellé brut ne doit pouvoir le recouvrir.
  const campagne = campagneCourante();
  const event: TrackedEvent = {
    name,
    ts: new Date().toISOString(),
    sid: SID,
    props: campagne ? { ...props, campagne } : props,
  };
  EVENTS.push(event);
  // Meta ne reçoit que ce qui sert l'optimisation des annonces, et seulement
  // avec le consentement de la personne (voir `meta.ts`).
  relayerMeta(name, props);
  // Le résultat affiché fait paraître le bandeau Meta (voir `meta.ts`).
  if (name === "score_shown" || name === "dirigeant_resultat") marquerParcoursEngage();

  if (CONFIG.analyticsEndpoint) {
    const body = JSON.stringify(event);
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(CONFIG.analyticsEndpoint, body);
      } else {
        void fetch(CONFIG.analyticsEndpoint, { method: "POST", body, keepalive: true });
      }
    } catch {
      // La mesure ne doit jamais casser le parcours.
    }
  }
}
